import React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { getExperimentFlags, useGeneratePath, useStaticUrls } from 'platform-components';
import { useNewLabPathAccess } from 'common.components/TeacherHeader/lib/useNewLabPathAccess';
import { getSelectedSubjectOrDefault, getIsInformaticsTeacher } from 'store/courses/selectors';
import { useSelector } from 'react-redux';
import { mapMatchPath } from 'common.components/TeacherHeader/lib/mapMatchPath';
import { getFeatureFlags } from 'platform-components/src/components/app-factory/selectors';
import { RedirectToNewLab } from 'common.components/TeacherHeader/lib/RedirectToNewLab';
import { isInIframe } from 'utils/isInIframe';

interface AuthProviderProps {
    children: React.ReactNode;
}

// eslint-disable-next-line complexity
export const AuthProvider = ({ children }: AuthProviderProps) => {
    const experimentsFlags = useSelector(getExperimentFlags);

    const { classId, subject: subjectFromParams } = useParams<{ classId: string; subject: string }>();

    const subject = useSelector((store) => getSelectedSubjectOrDefault(store, { classId, subject: subjectFromParams }));
    const isInformaticsTeacher = useSelector(getIsInformaticsTeacher);

    const staticUrls = useStaticUrls();

    const featureFlags = useSelector(getFeatureFlags);

    const {
        cmHome,
        labLessons,
        labJournal,
        labClassJournal,
        labSubjectJournal,
        labClassLessons,
        labCourseJournal,
        labStudentSubject,
        labTeacherTrainingCourses,
        labClass,
        labClasses,
        labSpecCourse,
        labClassSpecCourseStub,
        labClassSpecCourseLesson,
        labPortfolio,
        labLibrarySubjectCollection,
        labLibrarySubjectCommonCourseContainer,
        deepLabLibrary,
        labLibrarySubject,
        deepLabLibraryMain,
        labLibrary,
        labTeacherReserve,
        labClassTeacherReserve,
        labSubjectLessonsComplete,
        labSubjectLessonsCalendar,
        labLibrarySubjectMain,
        deepLabTheme,
        labLibrarySubjectTheme,
        labClassStudents,
        labPersonalAccountMain,
        labPersonalAccountTeamMain,
        newLabLiveEvents,
        newLabTeamsLiveEvents,
        labLibraryTeamMain,
    } = staticUrls;
    const config = {
        cmHome,
        labLessons,
        labJournal,
        labClassJournal,
        labSubjectJournal,
        labClassLessons,
        labCourseJournal,
        labStudentSubject,
        labTeacherTrainingCourses,
        labClass,
        labClasses,
        labClassStudents,
        labSpecCourse,
        labClassSpecCourseStub,
        labClassSpecCourseLesson,
        labPortfolio,
        labLibrarySubjectCollection,
        labLibrarySubjectCommonCourseContainer,
        deepLabLibrary,
        labLibrarySubject,
        deepLabLibraryMain,
        labLibrary,
        labLibrarySubjectMain,
        labTeacherReserve,
        labClassTeacherReserve,
        labSubjectLessonsComplete,
        labSubjectLessonsCalendar,
        deepLabTheme,
        labLibrarySubjectTheme,
        labPersonalAccountMain,
        labPersonalAccountTeamMain,
        newLabLiveEvents,
        newLabTeamsLiveEvents,
        labLibraryTeamMain,
    };
    const location = useLocation();
    const generatePath = useGeneratePath();
    const generateNewLabPath = (path: string) => generatePath(path, { teamId: classId, subject });

    const isMatchPath: Record<string, boolean> = mapMatchPath(config, location.pathname);

    const { newLabAccess, newLabPath } = useNewLabPathAccess({
        featureFlags,
        experimentsFlags,
        teamId: classId,
        subject,
        isInformaticsTeacher,
        staticUrls,
    });

    // публичная витрина
    if (isMatchPath.deepLabLibraryMain || isMatchPath.labLibrary) {
        if (newLabAccess.publicShowcase) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.publicShowcase));
        }
    }

    if (
        subject !== 'main' &&
        !isMatchPath.deepLabLibraryMain &&
        !isMatchPath.labLibrarySubjectMain &&
        (isMatchPath.deepLabTheme || isMatchPath.labLibrarySubjectTheme)
    ) {
        if (newLabAccess.materials) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.themes));
        }
    } else if (
        subject !== 'main' &&
        !isMatchPath.deepLabLibraryMain &&
        !isMatchPath.labLibrarySubjectMain &&
        (isMatchPath.labLibrarySubjectCollection || isMatchPath.labLibrarySubjectCommonCourseContainer)
    ) {
        if (newLabAccess.materials) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.container));
        }
    } else if (
        subject !== 'main' &&
        !isMatchPath.deepLabLibraryMain &&
        !isMatchPath.labLibrarySubjectMain &&
        (isMatchPath.labLibrarySubject ||
            isMatchPath.deepLabLibrary ||
            isMatchPath.deepLabTheme ||
            isMatchPath.deepLabThemeLessons ||
            isMatchPath.deepLabThemeLesson ||
            isMatchPath.labClassLibraryCalendarPlanShowcaseLesson)
    ) {
        if (newLabAccess.materials) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.materials));
        }
    } else if (
        isMatchPath.labClassJournal ||
        isMatchPath.labSubjectJournal ||
        isMatchPath.labCourseJournal ||
        isMatchPath.labStudentSubject
    ) {
        if (newLabAccess.journal) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.journal));
        }
    } else if (isMatchPath.labTeacherTrainingCourses) {
        if (newLabAccess.KPKs) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.KPKs));
        }
    } else if (isMatchPath.labPortfolio) {
        if (newLabAccess.portfolio) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.portfolio));
        }
    } else if (isMatchPath.labTeacherReserve || isMatchPath.labClassTeacherReserve) {
        if (newLabAccess.hrReserve) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.hrReserve));
        }
    } else if (!isInIframe() && (isMatchPath.labLessons || isMatchPath.labClassLessons)) {
        if (newLabAccess.lessons) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.lessons));
        }
    } else if (isMatchPath.labClassSpecCourseLesson) {
        if (newLabAccess.specProjects) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.specProject));
        }
    } else if (isMatchPath.labSpecCourse || isMatchPath.labClassSpecCourseStub) {
        if (newLabAccess.specProjects) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.specProjects));
        }
    } else if (isMatchPath.labClass) {
        if (newLabAccess.teams) {
            return RedirectToNewLab(generateNewLabPath(newLabPath.teams));
        }
    } else if (isMatchPath.labPersonalAccountMain) {
        if (newLabAccess.profile) {
            RedirectToNewLab(generateNewLabPath(newLabPath.profile));
        }
    } else if (isMatchPath.newLabLiveEvents) {
        if (newLabAccess.liveEvents) {
            RedirectToNewLab(generateNewLabPath(newLabPath.liveEvents));
        }
    } else if (isMatchPath.labLibrary || isMatchPath.labLibraryTeamMain) {
        if (newLabAccess.showcase) {
            RedirectToNewLab(generateNewLabPath(newLabPath.showcase));
        }
    }

    return <>{children}</>;
};
